import { useCallback, useEffect } from 'react'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import StripeConnectEmbeddedComponentManager from '@/components/stripe-connect/StripeConnectEmbeddedComponentManager'
import { canUseStripeConnectOnboarding } from '@/components/stripe-connect/stripeConnectStatus'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { stripeOnboardingLink } from '@/lib/stripe-financial-routes'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import {
  stripeConnectQueries,
  useStripeConnectStatus,
} from '@/lib/services/stripe-connect/stripe-connect.query'
import { notFound, redirect, useNavigate, useParams } from '@tanstack/react-router'
import { AlertCircleIcon } from 'lucide-react'

export async function dashboardlessStripeRouteLoader({ context, params }) {
  if (!context.ability.can('edit', 'stripe-account')) {
    throw notFound()
  }

  await assertNotSalesDemoOrganization(context.queryClient, params.orgId)

  const stripeConnectStatus = await context.queryClient.fetchQuery(
    stripeConnectQueries.status({ organizationId: params.orgId, refresh: true })
  )

  if (canUseStripeConnectOnboarding(stripeConnectStatus?.status)) {
    throw redirect(stripeOnboardingLink(params.orgId))
  }

  if (stripeConnectStatus?.connect_account?.account_version !== 'v2') {
    throw notFound()
  }
}

function DashboardlessStripeContent({ description, icon, sections, surface, title }) {
  const { orgId } = useParams({ strict: false })
  const navigate = useNavigate()
  const { data: stripeConnectStatus } = useStripeConnectStatus({
    refresh: true,
    refetchOnMount: false,
  })
  const shouldUseOnboarding = canUseStripeConnectOnboarding(stripeConnectStatus?.status)

  useEffect(() => {
    if (shouldUseOnboarding) {
      void navigate({
        ...stripeOnboardingLink(orgId),
        replace: true,
      })
    }
  }, [navigate, orgId, shouldUseOnboarding])

  const handleSessionConflict = useCallback(
    (nextStatus) => {
      if (canUseStripeConnectOnboarding(nextStatus?.status)) {
        void navigate({
          ...stripeOnboardingLink(orgId),
          replace: true,
        })
      }
    },
    [navigate, orgId]
  )

  if (shouldUseOnboarding) {
    return null
  }

  return (
    <div className="space-y-6 pb-6">
      <AdminPageHeader icon={icon} title={title} description={description} />

      {sections.map((section) => (
        <StripeConnectEmbeddedComponentManager
          {...section}
          key={section.title}
          organizationId={orgId}
          onSessionConflict={handleSessionConflict}
          showSectionHeader={sections.length > 1}
          surface={surface}
        />
      ))}
    </div>
  )
}

export function DashboardlessStripePage(props) {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <Alert variant="info">
          <AlertCircleIcon />
          <AlertTitle>This is the global org Stripe page</AlertTitle>
          <AlertDescription>Do not set up a Stripe connect account.</AlertDescription>
        </Alert>
      }
    >
      <DashboardlessStripeContent {...props} />
    </IfGlobalOrg>
  )
}
